/**
 * Rx Status Codes and Constants
 *
 * These constants define the prescription status codes used throughout the application.
 * Status codes are retrieved from the Kroll pharmacy system via MQTT queries.
 *
 * Valid statuses allow prescriptions to be dispensed.
 * Invalid statuses indicate cancelled, deleted, or problematic prescriptions.
 */

export const VALID_RX_STATUSES = [1, 3, 4, 6, 8];
export const INVALID_RX_STATUSES = [2, 5, 7];

export enum RxStatusCode {
  ACTIVE = 1,
  CANCELLED = 2,
  UNFILLED = 3,
  NOT_DISPENSED = 4,
  DELETED_VOID = 5,
  ON_FILE = 6,
  SUSPENDED = 8,
}

export const RX_STATUS_LABELS: Record<number, string> = {
  1: 'Active',
  2: 'Cancelled',
  3: 'Unfilled',
  4: 'Not Dispensed',
  5: 'Deleted/Void',
  6: 'On File',
  // Kroll's expired-service cancellation — was falling through to "Status 7"
  // everywhere except the Patient 360 prescriptions grid, which carried its own map.
  7: 'Exp. Svc (Cancelled)',
  8: 'Suspended',
};

export const RX_STATUS_DESCRIPTIONS: Record<number, string> = {
  1: 'Active - Valid for dispensing',
  2: 'Cancelled - INVALID - Do not dispense',
  3: 'Unfilled - Valid, waiting to be filled',
  4: 'Not Dispensed - Valid, filled but not yet dispensed',
  5: 'Deleted/Void - INVALID - Do not dispense',
  6: 'On File - Valid for future use',
  8: 'Suspended - Valid but temporarily on hold',
};

/**
 * Check if a given Rx status code is valid for dispensing
 */
export const isValidRxStatus = (status?: number): boolean => {
  if (status === undefined || status === null) return false;
  return VALID_RX_STATUSES.includes(status);
};

/**
 * Get the human-readable label for an Rx status code
 */
export const getRxStatusLabel = (status?: number): string => {
  if (status === undefined || status === null) return 'Unknown';
  return RX_STATUS_LABELS[status] || `Status ${status}`;
};

/**
 * Get the detailed description for an Rx status code
 */
export const getRxStatusDescription = (status?: number): string => {
  if (status === undefined || status === null) return 'No status information available';
  return RX_STATUS_DESCRIPTIONS[status] || `Unknown Status Code: ${status}`;
};

/**
 * Get the Material-UI color for a given Rx status code
 */
export const getRxStatusColor = (status?: number): 'error' | 'warning' | 'success' | 'default' => {
  if (status === undefined || status === null) return 'default';
  if (INVALID_RX_STATUSES.includes(status)) return 'error'; // Red
  if (status === 8) return 'warning'; // Yellow
  return 'success'; // Green
};

/**
 * Get the active/inactive category label for a given Rx status code
 */
export const getRxStatusCategoryLabel = (status?: number): 'ACTIVE' | 'INACTIVE' => {
  if (status === undefined || status === null) return 'INACTIVE';
  return INVALID_RX_STATUSES.includes(status) ? 'INACTIVE' : 'ACTIVE';
};

/**
 * Group medications by status category
 */
export const getMedicationsByStatusCategory = (medications: any[]) => {
  return {
    invalid: medications.filter(med => INVALID_RX_STATUSES.includes(med.status)),
    suspended: medications.filter(med => med.status === 8),
    active: medications.filter(med =>
      med.status && ![2, 5, 7, 8].includes(med.status) && isValidRxStatus(med.status)
    ),
  };
};

/**
 * Comprehensive Rx status information for display purposes
 */
export const RX_STATUS_FULL_INFO: Record<number, {
  code: number;
  label: string;
  description: string;
  category: 'ACTIVE' | 'INACTIVE';
  color: 'error' | 'warning' | 'success';
}> = {
  1: { code: 1, label: 'Active', description: 'Valid for dispensing', category: 'ACTIVE', color: 'success' },
  2: { code: 2, label: 'Cancelled', description: 'INVALID - Do not dispense', category: 'INACTIVE', color: 'error' },
  3: { code: 3, label: 'Unfilled', description: 'Valid, waiting to be filled', category: 'ACTIVE', color: 'success' },
  4: { code: 4, label: 'Not Dispensed', description: 'Valid, filled but not yet dispensed', category: 'ACTIVE', color: 'success' },
  5: { code: 5, label: 'Deleted/Void', description: 'INVALID - Do not dispense', category: 'INACTIVE', color: 'error' },
  6: { code: 6, label: 'On File', description: 'Valid for future use', category: 'ACTIVE', color: 'success' },
  7: { code: 7, label: 'Unknown/Invalid', description: 'INVALID status', category: 'INACTIVE', color: 'error' },
  8: { code: 8, label: 'Suspended', description: 'Valid but temporarily on hold', category: 'ACTIVE', color: 'warning' },
};
